import axios from 'axios';
import moment from 'moment';
import 'moment/locale/es';
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Swal from 'sweetalert2';
import DataTable from '../components/DataTable';

moment.locale('es');

const ListarInformesContractuales = () => {
  const { id } = useParams();
  const [informes, setInformes] = useState([]);
  const navigate = useNavigate();

  const generarYDescargarInforme = async (idInforme, documentoInstructor, rp, fechaInicio, mes) => {
    try {
      const response = await axios.get(`/api/informe-contractual/generar-docx/${idInforme}`, {
        responseType: 'blob',
      });

      const anioInicio = fechaInicio ? moment(fechaInicio).format('YYYY') : 'N-A';
      const mesFormatted = mes ? mes.toUpperCase() : 'SIN_MES';
      const rpValue = rp !== 'N/A' ? rp : 'SINRP';
      const docInstr = documentoInstructor !== 'N/A' ? documentoInstructor : 'SIN_DOC';

      const fileName = `GC_${docInstr}_${rpValue}_${mesFormatted}_${anioInicio}.docx`;

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Error al generar el informe:', error.message);
      Swal.fire('Error', 'No se pudo generar el informe.', 'error');
    }
  };

  const getData = useCallback(async () => {
    try {
      const response = await axios.get(`/api/informecontractual/listbycontrato/${id}`);
      let data = response.data.informes || [];

      data.sort((a, b) => new Date(a.fechaInforme) - new Date(b.fechaInforme));

      const dataConItem = data.map((informe, index) => ({
        ...informe,
        item: index + 1,
      }));

      const informesConFunciones = dataConItem.reverse().map((informe) => {
        const documentoInstructor = informe.documentoInstructor ?? 'N/A';
        const rp = informe.rp ?? 'N/A';
        const fechaInicio = informe.fechaInicio ?? null;
        const mes = informe.mes ?? null;

        return {
          ...informe,
          fechaInforme: moment.utc(informe.fechaInforme).add(5, 'hours').format('DD/MM/YYYY'),
          funciones: (
            <div className="d-flex gap-2 justify-content-center">
              <button
                className="btn btn-success"
                style={{ width: '30px', height: '30px', padding: '0' }}
                onClick={() => generarYDescargarInforme(informe._id, documentoInstructor, rp, fechaInicio, mes)}
                title="Descargar Informe Contractual"
              >
                <i className="fa-solid fa-file-word fa-lg" style={{ color: '#ffffff' }}></i>
              </button>

              <button
                className="btn btn-warning d-flex align-items-center justify-content-center"
                style={{ width: '30px', height: '30px', padding: '0' }}
                onClick={() => navigate(`/instructor/informecontractual/editar/${informe._id}`)}
                title="Editar Informe"
              >
                <i className="fa-solid fa-edit"></i>
              </button>

              <button
                className="btn btn-danger d-flex align-items-center justify-content-center"
                style={{ width: '30px', height: '30px', padding: '0' }}
                onClick={() => deleteInforme(informe._id)}
                title="Eliminar Informe"
              >
                <i className="fa-solid fa-trash"></i>
              </button>
            </div>
          ),
        };
      });

      setInformes(informesConFunciones);
    } catch (error) {
      console.error('Error al obtener los informes:', error.message);
    }
    // eslint-disable-next-line
  }, [id]);

  useEffect(() => {
    if (id) getData();
  }, [id, getData]);

  const deleteInforme = async (informeId) => {
    try {
      const result = await Swal.fire({
        title: '¿Está seguro?',
        text: '¡Esta acción eliminará el informe de forma permanente!',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: 'Sí, eliminar',
      });

      if (result.isConfirmed) {
        const { data } = await axios.delete(`/api/informecontractual/delete/${informeId}`);
        getData();
        Swal.fire({
          icon: 'success',
          title: data.message || 'Informe eliminado',
          showConfirmButton: false,
          timer: 1500,
        });
      }
    } catch (error) {
      console.error('Error al eliminar el informe:', error.message);
      Swal.fire('Error', 'No se pudo eliminar el informe.', 'error');
    }
  };

  const columns = React.useMemo(
    () => [
      { Header: 'Item', accessor: 'item' },
      { Header: 'Ciudad', accessor: 'ciudad' },
      { Header: 'Fecha del Informe', accessor: 'fechaInforme' },
      { Header: 'Mes', accessor: 'mes' },
      { Header: 'Año', accessor: 'anio' },
      { Header: 'N° Planilla', accessor: 'numeroPlanilla' },
      { Header: 'Periodo', accessor: 'periodoPlanilla' },
      { Header: 'Operador', accessor: 'operadorPlanilla' },
      { Header: 'Viajes', accessor: 'totalDesplazamientos' },
      { accessor: 'funciones' },
    ],
    []
  );

  return (
  <div className="container-fluid py-1 px-0 shadow-lg rounded-4 bg-light mt-5">
       <div className="d-flex align-items-center mb-3">
        <button
          style={{ marginTop: '24px', marginLeft: '32px' }}
          className="btn btn-outline-dark rounded-pill shadow-sm px-3 d-flex align-items-center gap-2 me-3"
          onClick={() => navigate(-1)}
          title="Regresar"
        >
       <i class="fa-solid fa-arrow-rotate-left"></i>
          <span>Volver</span>
        </button>

        <h2 className="text-center flex-grow-1 m-0">Informes Mensuales Registrados</h2>
      </div>

      {Array.isArray(informes) && informes.length > 0 ? (
        <DataTable columns={columns} data={informes} />
      ) : (
        <div className="alert alert-info text-center">
          No hay informes registrados para este contrato.
        </div>
      )}
    </div>
  );
};

export default ListarInformesContractuales;
